import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Divider,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@material-ui/core';

import {
  registerSefiDevice,
  getMySefiDevice,
  listSefiDevices,
  approveSefiDevice,
  renewSefiDevice,
  revokeSefiDevice,
  transferSefiDevice,
  getMyTailscaleEnrollment,
  listTailscaleEnrollments,
  approveTailscaleEnrollment,
  revokeTailscaleEnrollment
} from '../../_apis_/aiAnalytics';

// ----------------------------------------------------------------------
// Desktop Settings sections: device license, Owner device management,
// Tailscale status, application updates. Rendered inside SefiDesktopSettings.

const desktop = () => (typeof window !== 'undefined' ? window.desktop : undefined);
const d0 = () => desktop() || { network: {} };
const errMsg = (e, fallback) => (e && (e.message || (e.response && e.response.data && e.response.data.message))) || fallback;
const routeWarning = (net) =>
  net && net.apiRoute === 'public-fallback'
    ? 'Tailscale disconnected: using the public backend (allowed by configuration).'
    : null;
const fmt = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const Row = ({ label, value, bold }) => (
  <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
    <Typography variant="body2">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400, textAlign: 'right', wordBreak: 'break-all', ml: 1 }}>
      {value}
    </Typography>
  </Stack>
);
Row.propTypes = { label: PropTypes.node, value: PropTypes.node, bold: PropTypes.bool };

const Section = ({ title, children }) => (
  <>
    <Divider sx={{ my: 2 }} />
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
    {children}
  </>
);
Section.propTypes = { title: PropTypes.node, children: PropTypes.node };

const Note = ({ children, tone = 'text.secondary' }) => (
  <Typography variant="caption" color={tone} sx={{ display: 'block', mt: 1 }}>{children}</Typography>
);
Note.propTypes = { children: PropTypes.node, tone: PropTypes.string };

// ---------------- Update state hook (also drives the gear badge) ----------------
export function useDesktopUpdateState() {
  const [update, setUpdate] = useState(null);
  useEffect(() => {
    const d = desktop();
    if (!d || !d.getUpdateState) return undefined;
    let alive = true;
    d.getUpdateState().then((s) => alive && setUpdate(s)).catch(() => {});
    const off = d.onUpdateEvent ? d.onUpdateEvent((s) => alive && s && s.status && setUpdate(s)) : () => {};
    return () => { alive = false; off(); };
  }, []);
  return update;
}

// ---------------- Device license (this device) ----------------
function DeviceLicense({ facilityId }) {
  const [device, setDevice] = useState(null);
  const [state, setState] = useState('loading'); // loading|ready|unregistered|error
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const keyStatus = desktop().license.status ? await desktop().license.status() : { ok: true };
      if (!keyStatus.ok) {
        setState('error');
        setError(keyStatus.message || 'Secure key storage unavailable.');
        return;
      }
      setDevice(await getMySefiDevice());
      setState('ready');
    } catch (e) {
      if (e && e.code === 'SEFI_DEVICE_NOT_REGISTERED') { setState('unregistered'); setDevice(null); return; }
      setState('error');
      setError(errMsg(e, 'Unable to load device license.'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const request = async () => {
    setBusy(true);
    setError('');
    try {
      setDevice(await registerSefiDevice(facilityId));
      setState('ready');
    } catch (e) {
      setError(errMsg(e, 'Unable to register this device.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Device License">
      {state === 'loading' && <Typography sx={{ mt: 1 }} variant="body2">Loading device license...</Typography>}
      {state === 'unregistered' && (
        <>
          <Note>This device has no SEFI license yet. Request one; the Owner approves it.</Note>
          <Button fullWidth variant="contained" sx={{ mt: 1 }} disabled={busy} onClick={request}>
            Request Activation
          </Button>
        </>
      )}
      {state === 'ready' && device && (
        <>
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">Device Activation Code</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>{device.activation_code}</Typography>
          </Box>
          <Row label="Status" value={device.status_label} bold />
          <Row label="Days Remaining" value={device.days_remaining ?? 0} />
          <Row label="Valid Until" value={fmt(device.expires_at)} />
          {['Revoked', 'Transferred'].includes(device.status_label) && (
            <Button fullWidth variant="outlined" sx={{ mt: 1 }} disabled={busy} onClick={request}>Request Again</Button>
          )}
          {device.status_label === 'Pending' && <Note>Give this code to the Owner for approval.</Note>}
        </>
      )}
      {!!error && <Note tone="error.main">{error}</Note>}
      {state !== 'loading' && (
        <Button size="small" sx={{ mt: 1 }} onClick={load}>Refresh</Button>
      )}
    </Section>
  );
}
DeviceLicense.propTypes = { facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };

// ---------------- Owner: desktop devices ----------------
function OwnerDevices() {
  const [devices, setDevices] = useState([]);
  const [days, setDays] = useState(30);
  const [target, setTarget] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try {
      setDevices(await listSefiDevices());
      setError('');
    } catch (e) {
      setError(errMsg(e, 'Unable to load devices.'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okText) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await fn();
      setMessage(okText);
      await load();
    } catch (e) {
      setError(errMsg(e, 'Action failed.'));
    } finally {
      setBusy(false);
    }
  };

  const pending = devices.filter((d) => d.status_label === 'Pending');

  return (
    <Section title="Desktop Devices (Owner)">
      <TextField
        fullWidth size="small" type="number" label="Activation Days" sx={{ mt: 1.5 }}
        value={days} onChange={(e) => setDays(e.target.value)} inputProps={{ min: 1, max: 3650 }}
      />
      {devices.length === 0 && <Note>No desktop devices registered.</Note>}
      {devices.map((d) => (
        <Box key={d.id} sx={{ mt: 1.5, p: 1, borderRadius: 1, border: 1, borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {d.device_name || 'Desktop'} · {d.platform || '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
            {d.activation_code} · {d.user_name || `User ${d.user_id}`}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            {d.status_label}{d.status_label === 'Active' ? ` · ${d.days_remaining} days · until ${fmt(d.expires_at)}` : ''}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {['Pending', 'Expired'].includes(d.status_label) && (
              <Button size="small" variant="contained" disabled={busy}
                onClick={() => act(() => (d.status_label === 'Pending' ? approveSefiDevice(d.id, days) : renewSefiDevice(d.id, days)), 'Device approved.')}>
                {d.status_label === 'Pending' ? 'Approve' : 'Renew'}
              </Button>
            )}
            {d.status_label === 'Active' && (
              <Button size="small" variant="outlined" disabled={busy}
                onClick={() => act(() => renewSefiDevice(d.id, days), 'License renewed.')}>Renew</Button>
            )}
            {!['Revoked', 'Transferred'].includes(d.status_label) && (
              <Button size="small" color="error" disabled={busy}
                onClick={() => act(() => revokeSefiDevice(d.id), 'Device revoked.')}>Revoke</Button>
            )}
          </Stack>
          {d.status_label === 'Active' && pending.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <TextField select fullWidth size="small" label="Transfer to"
                value={target[d.id] || ''} onChange={(e) => setTarget({ ...target, [d.id]: e.target.value })}>
                {pending.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.device_name} · {p.activation_code}</MenuItem>
                ))}
              </TextField>
              <Button size="small" disabled={busy || !target[d.id]}
                onClick={() => act(() => transferSefiDevice(d.id, target[d.id]), 'License transferred.')}>Move</Button>
            </Stack>
          )}
        </Box>
      ))}
      {!!message && <Note tone="success.main">{message}</Note>}
      {!!error && <Note tone="error.main">{error}</Note>}
      <Button size="small" sx={{ mt: 1 }} onClick={load}>Refresh</Button>
    </Section>
  );
}

// ---------------- Tailscale ----------------
function NetworkStatus() {
  const [net, setNet] = useState(null);
  const [error, setError] = useState('');
  const [enrollment, setEnrollment] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      setNet(await desktop().network.getStatus());
      setError('');
    } catch (e) {
      setError('Unable to read Tailscale status.');
    }
    try {
      setEnrollment(await getMyTailscaleEnrollment());
    } catch (e) {
      setEnrollment(null); // device not licensed yet / server without enrollment
    }
  }, []);
  const connect = async () => {
    setBusy(true);
    setResult(null);
    try {
      setResult(await desktop().network.enroll()); // status + message only (no key)
    } catch (e) {
      setResult({ status: 'error', message: 'Enrollment failed.' });
    } finally {
      setBusy(false);
      load();
    }
  };
  const resultTone = (r) => ({ connected: 'success.main', pending: 'text.secondary' }[r.status] || 'error.main');
  useEffect(() => { load(); }, [load]);

  const ts = net && net.tailscale;
  let label = 'Checking...';
  if (ts) label = ts.connected ? 'Connected' : (ts.installed ? 'Disconnected' : 'Not installed');
  const route = {
    tailnet: 'Private (Tailscale)', public: 'Public HTTPS', 'public-fallback': 'Public HTTPS (fallback)', dev: 'Development'
  }[net && net.apiRoute] || '—';

  return (
    <Section title="Network">
      <Row label="Tailscale" value={label} bold />
      {ts && ts.connected && ts.dnsName && <Row label="This device" value={ts.dnsName} />}
      <Row label="Backend route" value={route} />
      {routeWarning(net) && <Note tone="warning.main">{routeWarning(net)}</Note>}
      {ts && !ts.connected && <Note tone={net.tailnetConfigured ? 'error.main' : 'text.secondary'}>{ts.message}</Note>}
      {net && net.tailnetConfigured && net.apiRoute !== 'tailnet' && (
        <Note>Private backend available when Tailscale is connected. Restart the app after connecting.</Note>
      )}
      {enrollment && enrollment.status_label && (
        <Row label="Network enrollment" value={`${enrollment.status_label}${enrollment.hostname ? ` · ${enrollment.hostname}` : ''}`} />
      )}
      {!!error && <Note tone="error.main">{error}</Note>}
      {result && <Note tone={resultTone(result)}>{result.message || result.status}</Note>}
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        {d0().network.enroll && !(ts && ts.connected && net.apiRoute === 'tailnet') && (
          <Button size="small" variant="contained" disabled={busy} onClick={connect}>
            {busy ? 'Connecting...' : 'Connect Tailscale'}
          </Button>
        )}
        <Button size="small" onClick={load}>Refresh</Button>
      </Stack>
    </Section>
  );
}

// ---------------- Owner: network enrollments ----------------
function OwnerNetworkEnrollments() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      setRows(await listTailscaleEnrollments());
      setError('');
    } catch (e) {
      setError(errMsg(e, 'Unable to load network enrollments.'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn) => {
    setBusy(true);
    setError('');
    try { await fn(); await load(); } catch (e) { setError(errMsg(e, 'Action failed.')); } finally { setBusy(false); }
  };
  return (
    <Section title="Network Enrollments (Owner)">
      {rows.length === 0 && <Note>No Tailscale enrollment requests.</Note>}
      {rows.map((r) => (
        <Box key={r.id} sx={{ mt: 1.5, p: 1, borderRadius: 1, border: 1, borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.hostname} · {r.device_name || 'Desktop'}</Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            {r.status_label} · {r.user_name || `User ${r.requested_by}`}{r.node_id ? ' · node verified' : ''}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {r.status_label !== 'Active' && (
              <Button size="small" variant="contained" disabled={busy} onClick={() => act(() => approveTailscaleEnrollment(r.id))}>Approve</Button>
            )}
            {r.status_label !== 'Revoked' && (
              <Button size="small" color="error" disabled={busy} onClick={() => act(() => revokeTailscaleEnrollment(r.id))}>Revoke</Button>
            )}
          </Stack>
        </Box>
      ))}
      <Note>Revoke stops new connection keys. Remove an already-joined machine in the Tailscale admin console.</Note>
      {!!error && <Note tone="error.main">{error}</Note>}
      <Button size="small" sx={{ mt: 1 }} onClick={load}>Refresh</Button>
    </Section>
  );
}

// ---------------- Updates ----------------
function UpdatePanel({ update }) {
  const [busy, setBusy] = useState(false);
  const d = desktop();
  const s = update || {};
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { /* state event carries the message */ } finally { setBusy(false); }
  };
  const statusText = {
    checking: 'Checking...',
    available: 'Update available',
    'not-available': 'Up to date',
    downloading: `Downloading ${s.percent || 0}%`,
    verifying: 'Verifying signature...',
    downloaded: 'Ready to install (verified)',
    error: 'Update error',
    unsupported: 'Unavailable'
  }[s.status] || '—';

  return (
    <Section title="Application Update">
      <Row label="Installed Version" value={s.currentVersion || '—'} bold />
      <Row label="Available Version" value={s.availableVersion || '—'} />
      <Row label="Status" value={statusText} />
      {s.status === 'downloading' && <LinearProgress variant="determinate" value={s.percent || 0} sx={{ mt: 1 }} />}
      {!!s.message && <Note tone={s.status === 'error' ? 'error.main' : 'text.secondary'}>{s.message}</Note>}
      {s.installKind === 'deb' && s.status === 'downloaded' && (
        <Note>Installing asks for your system password (package manager).</Note>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Button fullWidth variant="outlined" disabled={busy || ['checking', 'downloading', 'verifying', 'unsupported'].includes(s.status)}
          onClick={() => run(() => d.checkForUpdates())}>Check</Button>
        {s.status === 'available' && (
          <Button fullWidth variant="contained" disabled={busy} onClick={() => run(() => d.downloadUpdate())}>Download</Button>
        )}
        {s.status === 'downloaded' && (
          <Button fullWidth variant="contained" disabled={busy} onClick={() => run(() => d.installUpdate())}>Restart &amp; Install</Button>
        )}
      </Stack>
    </Section>
  );
}
UpdatePanel.propTypes = { update: PropTypes.object };

// ---------------- Composite ----------------
export default function DesktopStatusPanels({ isOwner, facilityId, update }) {
  const d = desktop();
  const isDesktop = !!(d && d.isElectron && d.license);
  return (
    <>
      {isDesktop && <DeviceLicense facilityId={facilityId} />}
      {isOwner && <OwnerDevices />}
      {isOwner && <OwnerNetworkEnrollments />}
      {isDesktop && d.network && <NetworkStatus />}
      {isDesktop && <UpdatePanel update={update} />}
    </>
  );
}
DesktopStatusPanels.propTypes = {
  isOwner: PropTypes.bool,
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  update: PropTypes.object
};
